import { LitElement, css, html } from "lit";
import { defineChromeElement } from "../../chrome/ChromeElement";
import { artImage, artMask, type ChromeArt } from "../../chrome/chromeArt";
import { chromeControlStyles } from "../../chrome/chromeStyles";
import type { SpinButtonBindings, SpinButtonPort } from "./SpinButtonPort";

/** `spinIcon` insets inside the 300² button (Widget top/bottom 40.73, left/right 21.24); `slamIcon` insets 61.58. */
const SPIN_ICON_INSET = { x: 21.24, y: 40.73 } as const;
const SLAM_ICON_INSET = 61.58;
/** The hold warm-up: the faces scale ×2 over 1 s (authored on the prefab); the hold threshold (0.18 s by default) stops it. */
const WARMUP = { scale: 2, seconds: 1 } as const;
const SPIN_COLORS = { default: "#ffffff", featureMode: "#fc8404" } as const;
const SLAM_COLOR = "#ff4141";
/**
 * The releases that end a press. They are heard on the window in the capture phase, from the press
 * until the first of them. A mouse released off the face sends its pointerup elsewhere; a finger's
 * implicit capture sends it to the face wherever it lifts; mid-hold the slam face replaces the spin
 * face. So where the release landed is read from its coordinates, never from which listener heard
 * it. The legacy types make sure at least one fires on every device and emulation mode.
 */
const RELEASE_EVENTS = ["pointerup", "pointercancel", "mouseup", "touchend", "touchcancel"] as const;

export const SPIN_BUTTON_TAG = "slot-spin-button";

/**
 * `<slot-spin-button>`: the spin / slam control. A press on the spin face is reported at
 * pointer-down, and its end at the first release after it, anywhere, with whether it landed on the
 * face; only such a release can be a tap (decision 25). The slam face replaces the spin face while a
 * spin can be slammed, the warm-up scale rides a CSS transition on the inner face. The bar sets its
 * `size` per shape (300 portrait, 240 desktop, 239 landscape). The idle hold hint is not on the
 * button: the bar composes the `SpinButtonPort` and draws it as its caption (decision 36).
 */
export class SpinButtonElement extends LitElement implements Omit<SpinButtonPort, "setHoldHintVisible"> {
  public static override properties = {
    art: { attribute: false },
    size: { type: Number },
    shown: { type: Boolean, reflect: true },
    slam: { type: Boolean, reflect: true },
    enabled: { type: Boolean, reflect: true },
    featureMode: { type: Boolean, reflect: true, attribute: "feature-mode" },
    warm: { type: Boolean, reflect: true },
    instant: { type: Boolean, reflect: true },
  };

  public declare art: ChromeArt;
  public declare size: number;
  public declare shown: boolean;
  public declare slam: boolean;
  public declare enabled: boolean;
  public declare featureMode: boolean;
  private declare warm: boolean;
  private declare instant: boolean;
  private bindings: SpinButtonBindings | null = null;

  public constructor() {
    super();
    this.art = {};
    this.size = 300;
    this.shown = true;
    this.slam = false;
    this.enabled = true;
    this.featureMode = false;
    this.warm = false;
    this.instant = false;
  }

  public static override styles = [
    chromeControlStyles,
    css`
      :host { display: block; position: relative; }
      :host(:not([shown])) { display: none; }
      /* The scale rides an inner node: the bar positions the host with a transform of its own, and an outer-context declaration beats any \`:host\` one. */
      .face { position: absolute; inset: 0; transition: transform ${WARMUP.seconds}s linear; }
      :host([warm]) .face { transform: scale(${WARMUP.scale}); }
      :host([instant]) .face { transition: none; }
      button { position: absolute; inset: 0; border-radius: 50%; }
      .back { position: absolute; inset: 0; }
      .icon { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); }
      button:hover .icon { filter: brightness(0.86); }
      button:active .icon { filter: brightness(0.7); }
      button[disabled] .icon { filter: brightness(0.5); }
      :host([slam]) .spin, :host(:not([slam])) .slam { display: none; }
    `,
  ];

  public bind(bindings: SpinButtonBindings): void {
    this.bindings = bindings;
  }

  public setLocked(locked: boolean): void {
    this.enabled = !locked;
  }

  public setVisible(visible: boolean): void {
    this.shown = visible;
  }

  public setSlamVisible(visible: boolean): void {
    this.slam = visible;
    if (visible) this.stopHoldWarmupVisual();
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  public setFeatureModeStyle(active: boolean): void {
    this.featureMode = active;
  }

  public playHoldWarmupVisual(): void {
    this.instant = false;
    this.warm = true;
  }

  public stopHoldWarmupVisual(): void {
    this.instant = false;
    this.warm = false;
  }

  public resetHoldWarmupVisualImmediate(): void {
    this.instant = true;
    this.warm = false;
  }

  private onPointerDown = (): void => {
    if (!this.enabled) return;
    for (const type of RELEASE_EVENTS) window.addEventListener(type, this.onRelease, true);
    this.bindings?.onPressStart?.();
  };

  /**
   * The slam face acts on pointer-down, like the spin face (decision 25). It used to act on `click`: a finger's tap on
   * the spin face starts the spin, the face swaps to slam while the finger is still down (a stop is allowed from
   * `requesting`), and the tap's trailing compatibility `click` is dispatched to whatever is under the lifted finger —
   * the slam face — so every touch tap slammed the spin it had just started (staging, 2026-10-04). A mouse never did:
   * its `click` goes to the common ancestor of the mousedown and mouseup targets, which is neither face. A compat
   * `click` carries no pointer-down of its own, so only a NEW press reaches `onSlamClick` now; the keyboard's
   * activation of the focused face (Enter / Space: a `click` with `detail` 0) still slams.
   */
  private onSlamPointerDown = (): void => {
    if (!this.enabled) return;
    this.bindings?.onSlamClick();
  };

  private onSlamClick = (e: MouseEvent): void => {
    if (!this.enabled || e.detail !== 0) return;
    this.bindings?.onSlamClick();
  };

  private onRelease = (e: Event): void => {
    for (const type of RELEASE_EVENTS) window.removeEventListener(type, this.onRelease, true);
    // A cancel is never a tap; a touch release reads the lifted finger. The face measures 0×0 while
    // the slam face replaces it, so a release then is off it.
    const point = e.type.endsWith("cancel") ? undefined : "changedTouches" in e ? (e as TouchEvent).changedTouches[0] : (e as MouseEvent);
    const face = this.renderRoot.querySelector("[data-part=spin]")!.getBoundingClientRect();
    this.bindings?.onPressEnd?.(point !== undefined && point.clientX > face.left && point.clientX < face.right && point.clientY > face.top && point.clientY < face.bottom);
  };

  protected override render() {
    const s = this.size;
    const spinIcon = { width: s - 2 * SPIN_ICON_INSET.x, height: s - 2 * SPIN_ICON_INSET.y };
    const slamIcon = s - 2 * SLAM_ICON_INSET;
    return html`
      <div class="face">
        <button class="spin" data-part="spin" type="button" ?disabled=${!this.enabled} @pointerdown=${this.onPointerDown}>
          <div class="back art" style=${artImage(this.art, "spinBtnBack")}></div>
          <div class="icon mask" style="width: ${spinIcon.width}px; height: ${spinIcon.height}px; ${artMask(this.art, "spinIcon", this.featureMode ? SPIN_COLORS.featureMode : SPIN_COLORS.default)}"></div>
        </button>
        <button class="slam" data-part="slam" type="button" ?disabled=${!this.enabled} @pointerdown=${this.onSlamPointerDown} @click=${this.onSlamClick}>
          <div class="back art" style=${artImage(this.art, "spinBtnBack")}></div>
          <div class="icon mask" style="width: ${slamIcon}px; height: ${slamIcon}px; ${artMask(this.art, "slamIcon", SLAM_COLOR)}"></div>
        </button>
      </div>
    `;
  }
}

export function defineSpinButtonElement(): void {
  defineChromeElement(SPIN_BUTTON_TAG, SpinButtonElement);
}
